import { action, observable } from 'mobx';
import { IMenuItemReport, RevenueReportReqParams, SortProperty } from './types';
import Log from 'helpers/log';
import httpFacade from 'http/httpFacade';
import IReportStore from './IReportStore';
import { downloadPdfBlob } from 'helpers/pdf';
import { downloadCsvBlob } from 'helpers/csv';
import RootStore from 'stores/RootStore';
import { showDate } from 'helpers/datetime';

class ItemReportStore implements IReportStore {
  @observable report: IMenuItemReport[];
  @observable loading = false;
  sortProperty: SortProperty | null = { id: 'amount', desc: true };

  @action.bound
  async getReport(params: RevenueReportReqParams) {
    try {
      this.loading = true;

      const { data } = await httpFacade.order.fetchItemReport(params);

      this.report = data;
    } catch (error) {
      Log.warn(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  async getPdfReport(params: RevenueReportReqParams) {
    try {
      const fileName = RootStore.localization.formatMessage(
        'filename.report.item.pdf',
        {
          range:
            params.from !== params.to
              ? `${showDate(params.from)} - ${showDate(params.to)}`
              : showDate(params.from),
        },
      );

      const { data } = await httpFacade.order.fetchItemReportPdf({
        ...params,
        sortProperty: this.sortProperty?.id,
        desc: this.sortProperty !== null ? this.sortProperty?.desc : false,
      });
      const pdfBlobURL = URL.createObjectURL(data);
      const pdfBlob = new Blob([data], { type: 'application/pdf' });
      downloadPdfBlob({ fileName, pdfBlob, pdfBlobURL });
    } catch (error) {
      Log.error(error);
    }
  }

  @action.bound
  async getCsvReport(params: RevenueReportReqParams) {
    try {
      const fileName = RootStore.localization.formatMessage(
        'filename.report.item.csv',
        {
          range:
            params.from !== params.to
              ? `${showDate(params.from)} - ${showDate(params.to)}`
              : showDate(params.from),
        },
      );
      const { data } = await httpFacade.order.fetchItemReportCsv({
        ...params,
        sortProperty: this.sortProperty?.id,
        desc: this.sortProperty !== null ? this.sortProperty?.desc : false,
      });
      const csvBlobURL = URL.createObjectURL(data);
      const csvBlob = new Blob([data], { type: 'text/csv' });
      downloadCsvBlob({ fileName, csvBlob, csvBlobURL });
    } catch (error) {
      Log.error(error);
    }
  }

  @action.bound
  changeSortProperty(sortProperty: SortProperty | null) {
    this.sortProperty = sortProperty;
  }
}

export default ItemReportStore;
